export const ORG_SUBSCRIPTION_NOTE_MAX = 280;

export const SUBSCRIPTION_KEYS = {
  turboSales: "turbo_sales",
  turboManufacturing: "turbo_manufacturing",
  legacyMfgSuite: "manufacturing_suite",
  turboSign: "turbo_sign",
  coverage: "coverage",
} as const;

export type OrgSubscriptionStatus = "active" | "inactive";

export type OrgSubscriptionCatalogEntry = {
  key: string;
  label: string;
  grantable: boolean;
  implication: string;
};

export const ORG_SUBSCRIPTION_CATALOG: readonly OrgSubscriptionCatalogEntry[] = [
  {
    key: SUBSCRIPTION_KEYS.turboSales,
    label: "Turbo Sales",
    grantable: true,
    implication: "Anchor. An active row sets anchors.turbo_sales.",
  },
  {
    key: SUBSCRIPTION_KEYS.turboManufacturing,
    label: "Turbo Manufacturing",
    grantable: true,
    implication: "Anchor. An active row sets anchors.turbo_manufacturing.",
  },
  {
    key: SUBSCRIPTION_KEYS.legacyMfgSuite,
    label: "Manufacturing Suite",
    grantable: false,
    implication:
      "Legacy alias. An active row folds into Turbo Manufacturing only. This screen does not grant it.",
  },
  {
    key: SUBSCRIPTION_KEYS.turboSign,
    label: "Turbo Sign",
    grantable: true,
    implication:
      "An active Turbo Sales row already turns Sign on. A standalone row is for an organization without Turbo Sales.",
  },
  {
    key: SUBSCRIPTION_KEYS.coverage,
    label: "Coverage",
    grantable: true,
    implication:
      "Paid add-on. Not implied by Turbo Sales or Turbo Manufacturing. This screen only stores the entitlement row.",
  },
];

export type OrgEntitlementRow = {
  id: string;
  organization_id: string;
  addon_key: string;
  status: string;
  notes: string;
  granted_by_user_id: string | null;
  created_at: string;
  updated_at: string;
};

export type SubscriptionUpsert = {
  organizationId: string;
  addonKey: string;
  status: OrgSubscriptionStatus;
  notes: string;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const catalogKeys = new Set(ORG_SUBSCRIPTION_CATALOG.map((entry) => entry.key));
const grantableKeys = new Set(
  ORG_SUBSCRIPTION_CATALOG.filter((entry) => entry.grantable).map((entry) => entry.key),
);

export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

export function isKnownSubscriptionKey(key: string): boolean {
  return catalogKeys.has(key);
}

export function isGrantableSubscriptionKey(key: string): boolean {
  return grantableKeys.has(key);
}

export function catalogEntry(key: string): OrgSubscriptionCatalogEntry | undefined {
  return ORG_SUBSCRIPTION_CATALOG.find((entry) => entry.key === key);
}

/** Active means status exactly `active`. Inactive, missing, and any other status stay off. */
export function isSubscriptionActive(
  rows: Array<{ addon_key: string | null; status: string | null }>,
  addonKey: string,
): boolean {
  return rows.some((row) => row.addon_key === addonKey && row.status === "active");
}

export function parseSubscriptionUpsert(input: {
  organizationId: unknown;
  addonKey: unknown;
  status: unknown;
  notes: unknown;
}): { ok: true; value: SubscriptionUpsert } | { ok: false; error: string } {
  if (typeof input.organizationId !== "string" || !isUuid(input.organizationId)) {
    return { ok: false, error: "Organization id is required." };
  }
  if (typeof input.addonKey !== "string" || !isKnownSubscriptionKey(input.addonKey)) {
    return { ok: false, error: "Unknown subscription key." };
  }
  if (!isGrantableSubscriptionKey(input.addonKey)) {
    return { ok: false, error: "That subscription cannot be granted from this screen." };
  }
  if (input.status !== "active" && input.status !== "inactive") {
    return { ok: false, error: "Status must be active or inactive." };
  }
  if (input.notes != null && typeof input.notes !== "string") {
    return { ok: false, error: "Note must be text." };
  }
  const notes = typeof input.notes === "string" ? input.notes.trim() : "";
  if (notes.length > ORG_SUBSCRIPTION_NOTE_MAX) {
    return { ok: false, error: `Note must be ${ORG_SUBSCRIPTION_NOTE_MAX} characters or fewer.` };
  }
  return {
    ok: true,
    value: {
      organizationId: input.organizationId,
      addonKey: input.addonKey,
      status: input.status,
      notes,
    },
  };
}
